import {
  Box,
  Button,
  Dialog,
  Backdrop,
  MenuItem,
  useTheme,
  TextField,
  FormLabel,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Project } from 'library/types';
import apiService from 'library/api-service';
import { GridRowId } from '@mui/x-data-grid-premium';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCallback, useEffect, useState } from 'react';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM } from 'enums';

type UpdateProductProjectPlanFormParams = {
  projectId: Project | null;
  title: string;
  estimatedTaskDay: string;
  description: string;
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM;
  responsibleContactType: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM;
};

export type UpdateProductProjectPlanPostFormParams = Omit<
  UpdateProductProjectPlanFormParams,
  'projectId' | 'estimatedTaskDay'
> & {
  id: number;
  projectId: number;
  estimatedTaskDay: number;
};

const defaultValues: UpdateProductProjectPlanFormParams = {
  projectId: null,
  title: '',
  estimatedTaskDay: '',
  description: '',
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
  responsibleContactType: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
};

type UpdateProductProjectPlanDialogProps = {
  open: boolean;
  handleClose: () => void;
  productProjectPlanId: GridRowId;
  products: Project[];
  priorityOptions: {
    value: PROJECT_STEP_PRIORITY_TYPE_ENUM;
    label: string | undefined;
  }[];
  responsibleContactOptions: {
    value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM;
    label: string | undefined;
  }[];
  refresh: () => Promise<void>;
};

const UpdateProductProjectPlanDialog = ({
  open,
  handleClose: closeDialog,
  productProjectPlanId,
  products,
  priorityOptions,
  responsibleContactOptions,
  refresh,
}: UpdateProductProjectPlanDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);

  // form validations & form state
  const schema = yup
    .object({
      projectId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      title: yup
        .string()
        .trim()
        .max(
          50,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 50 }
          )
        )
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      estimatedTaskDay: yup
        .string()
        .trim()
        .required(
          intl.formatMessage({
            id: 'validations.required',
            defaultMessage: 'Bu alan boş bırakılamaz',
          })
        )
        .matches(
          /^(?:0\.[0-9]*[1-9][0-9]*|[1-9][0-9]*(?:\.[0-9]+)?)$/,
          intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          })
        ),
      description: yup
        .string()
        .trim()
        .max(
          250,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 250 }
          )
        )
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    })
    .required();

  const { reset, control, setValue, handleSubmit } = useForm<UpdateProductProjectPlanFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
  };

  // async helper functions
  const onSubmit = async (data: UpdateProductProjectPlanFormParams) => {
    if (!productProjectPlanId || !data.projectId) return;

    const postData: UpdateProductProjectPlanPostFormParams = {
      ...data,
      projectId: data.projectId.id,
      estimatedTaskDay: Number(data.estimatedTaskDay),

      id: productProjectPlanId as number,
    };

    try {
      setLoading(true);

      const response = await apiService.updateProductProjectPlan(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="productProjectPlan.updateProductProjectPlanCompleted"
            defaultMessage="Ürün - Proje Planı güncelleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSpecificProductProjectPlan = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificProductProjectPlan(productProjectPlanId as number);

      if (response.isSuccess) {
        const product = products.find((product) => product.id === response.data.projectId);

        if (product) {
          setValue('projectId', product);
        }

        setValue('title', response.data.title);
        setValue('estimatedTaskDay', String(response.data.estimatedTaskDay));
        setValue('description', response.data.description);
        setValue('priority', response.data.priority);
        setValue('responsibleContactType', response.data.responsibleContactType);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [productProjectPlanId, setValue, products]);

  // useEffect hooks
  useEffect(() => {
    if (open && productProjectPlanId) {
      getSpecificProductProjectPlan();
    }
  }, [open, productProjectPlanId, getSpecificProductProjectPlan]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="productProjectPlan.updateProductProjectPlan"
          defaultMessage="Ürün - Proje Planı Güncelle"
        />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="productProjectPlan.updateProductProjectPlanText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili Ürün - Proje Planı için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="projectId">
                <FormattedMessage id="project.table.product" defaultMessage="Ürün" />
              </FormLabel>
              <Controller
                name="projectId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={products}
                    value={value}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Project | null) => onChange(newValue)}
                    getOptionLabel={(option: Project) => option.title ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="priority">
                <FormattedMessage id="projectStepManaging.priority" defaultMessage="Öncelik" />
              </FormLabel>
              <Controller
                name="priority"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {priorityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="responsibleContactType">
                <FormattedMessage id="productProjectPlan.responsibleContact" defaultMessage="Sorumlu" />
              </FormLabel>
              <Controller
                name="responsibleContactType"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {responsibleContactOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="estimatedTaskDay">
                <FormattedMessage id="productProjectPlan.estimatedTaskDay" defaultMessage="Tahmini Adım Süresi (gün)" />
              </FormLabel>
              <Controller
                name="estimatedTaskDay"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormLabel htmlFor="title">
                <FormattedMessage id="projectStepManaging.projectStep" defaultMessage="Proje Adımı" />
              </FormLabel>
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormLabel htmlFor="description">
                <FormattedMessage id="projectStepManaging.stepExplanation" defaultMessage="Adım Açıklaması" />
              </FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default UpdateProductProjectPlanDialog;
